import React from 'react';
import styles from '../../utils/styles/components/AnalyticsTimelineChart/style.module.css';

const CHART_WIDTH = 880;
const CHART_HEIGHT = 260;
const PADDING = { top: 18, right: 20, bottom: 42, left: 22 };

function formatDay(day) {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short'
  }).format(new Date(`${day}T00:00:00`));
}

function buildLinePath(points) {
  if (points.length === 0) {
    return '';
  }

  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
}

function buildAreaPath(points, baselineY) {
  if (points.length === 0) {
    return '';
  }

  const linePath = buildLinePath(points);
  const startPoint = points[0];
  const endPoint = points[points.length - 1];

  return `${linePath} L ${endPoint.x} ${baselineY} L ${startPoint.x} ${baselineY} Z`;
}

function buildSeries(items, valueKey, chartWidth, chartHeight, maxValue) {
  const usableWidth = chartWidth - PADDING.left - PADDING.right;
  const usableHeight = chartHeight - PADDING.top - PADDING.bottom;
  const pointCount = Math.max(items.length - 1, 1);

  return items.map((item, index) => {
    const x = PADDING.left + ((usableWidth / pointCount) * index);
    const value = item[valueKey] || 0;
    const y = PADDING.top + usableHeight - ((value / maxValue) * usableHeight);

    return {
      day: item.day,
      label: formatDay(item.day),
      value,
      x,
      y
    };
  });
}

export const AnalyticsTimelineChart = ({ items }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className={styles.emptyState}>Aucune visite à afficher pour la période.</p>;
  }

  const peakValue = items.reduce((highest, item) => {
    return Math.max(highest, item.pageViews || 0, item.uniqueSessions || 0);
  }, 0);
  const maxValue = Math.max(peakValue, 1);
  const pageViewPoints = buildSeries(items, 'pageViews', CHART_WIDTH, CHART_HEIGHT, maxValue);
  const sessionPoints = buildSeries(items, 'uniqueSessions', CHART_WIDTH, CHART_HEIGHT, maxValue);
  const baselineY = CHART_HEIGHT - PADDING.bottom;
  const xLabels = items.filter((_, index) => {
    if (items.length <= 6) {
      return true;
    }

    if (index === 0 || index === items.length - 1) {
      return true;
    }

    return index % Math.ceil(items.length / 5) === 0;
  });

  return (
    <div className={styles.chartShell}>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.pageViewsSwatch}`} />
          Pages vues
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.sessionsSwatch}`} />
          Sessions
        </span>
      </div>

      <svg
        aria-label="Graphique d'évolution des visites"
        className={styles.chart}
        role="img"
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      >
        <defs>
          <linearGradient id="page-views-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(255, 188, 102, 0.42)" />
            <stop offset="100%" stopColor="rgba(255, 188, 102, 0)" />
          </linearGradient>
        </defs>

        {[0, 0.5, 1].map((ratio) => {
          const y = PADDING.top + ((CHART_HEIGHT - PADDING.top - PADDING.bottom) * ratio);

          return (
            <line
              className={styles.gridLine}
              key={ratio}
              x1={PADDING.left}
              x2={CHART_WIDTH - PADDING.right}
              y1={y}
              y2={y}
            />
          );
        })}

        <path className={styles.area} d={buildAreaPath(pageViewPoints, baselineY)} />
        <path className={styles.pageViewsLine} d={buildLinePath(pageViewPoints)} />
        <path className={styles.sessionsLine} d={buildLinePath(sessionPoints)} />

        {pageViewPoints.map((point) => (
          <g className={styles.pointGroup} key={`page-${point.day}`}>
            <circle className={styles.pagePoint} cx={point.x} cy={point.y} r="4.6" />
            <title>{`${point.label} • ${point.value} page vue${point.value > 1 ? 's' : ''}`}</title>
          </g>
        ))}

        {sessionPoints.map((point) => (
          <g className={styles.pointGroup} key={`session-${point.day}`}>
            <circle className={styles.sessionPoint} cx={point.x} cy={point.y} r="3.4" />
            <title>{`${point.label} • ${point.value} session${point.value > 1 ? 's' : ''}`}</title>
          </g>
        ))}

        {xLabels.map((item) => {
          const point = pageViewPoints.find((entry) => entry.day === item.day);

          return (
            <text className={styles.axisLabel} key={`label-${item.day}`} x={point?.x || 0} y={CHART_HEIGHT - 12}>
              {formatDay(item.day)}
            </text>
          );
        })}
      </svg>
    </div>
  );
};
